// CUSTOMER-COMMS-140 — post-order bento: confirmation → status → primary → share → secondary.
import { useEffect } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { emitEvent } from "./events-v2.js";
import { formatUah } from "./money-v2.js";
import { ReferralCardV2, type ReferralShareData } from "./ReferralCardV2.js";
import { t } from "./store-v2-ui-copy.js";

export type PaidBentoVariant = "online_paid" | "cod" | "fop_review";

export function PaidBentoDashboardV2({
  lang,
  displayOrderNumber,
  totalUah,
  paymentMethodLabel,
  fulfillmentLabel,
  referral,
  onOpenRules,
  onContactManager,
  onHome,
  onCatalog,
  onViewOrder,
  onReferralShared,
  onCloseStore,
  variant = "online_paid",
}: {
  lang: StoreV2Lang;
  displayOrderNumber: string;
  totalUah?: number | null;
  paymentMethodLabel: string;
  fulfillmentLabel: string;
  referral: ReferralShareData | null;
  /** @deprecated R8 — share no longer awaits createReferral */
  onCreateReferral?: () => void;
  onOpenRules?: () => void;
  onContactManager: () => void;
  onHome: () => void;
  onCatalog?: () => void;
  /** RC4 — primary paid action (exact order). */
  onViewOrder?: () => void;
  onReferralShared?: () => void;
  onCloseStore?: () => void;
  variant?: PaidBentoVariant;
}) {
  useEffect(() => {
    if (!referral) return;
    emitEvent("referral_card_impression", { code: referral.code, surface: "paid_bento", variant });
  }, [referral?.code, variant]);
  const amount =
    totalUah != null && Number.isFinite(totalUah) ? formatUah(Math.ceil(totalUah)) : "—";

  const heroTitle =
    variant === "cod"
      ? t(lang, "success_cod_title")
      : variant === "fop_review"
        ? t(lang, "success_fop_receipt_title")
        : t(lang, "success_paid_title");
  const paymentRow =
    variant === "cod"
      ? t(lang, "success_cod_payment_row")
      : variant === "fop_review"
        ? t(lang, "success_fop_receipt_status")
        : paymentMethodLabel;
  const paymentRowLabel =
    variant === "fop_review" ? t(lang, "fop_payment_status_label") : t(lang, "payment");
  const fulfillmentNextRow = fulfillmentLabel;
  const thankYou =
    variant === "cod"
      ? lang === "en"
        ? "Payment on delivery at Nova Poshta — not confirmed online."
        : "Оплата — під час отримання у Новій пошті."
      : variant === "fop_review"
        ? lang === "en"
          ? "Receipt does not prove payment by itself. A manager will verify the transfer."
          : "Квитанція сама по собі не підтверджує оплату. Менеджер перевірить переказ."
        : t(lang, "thank_you");

  return (
    <div
      className="sv2-bento"
      data-testid="sv2-paid-bento"
      data-bento-variant={variant}
      data-payment-confirmed={variant === "online_paid" ? "1" : "0"}
      data-layout="comms-140"
    >
      <div className="sv2-bento-top">
        <section className="sv2-bento-card sv2-bento-success" data-testid="sv2-paid-success-card">
          <div className="sv2-success-emoji" aria-hidden="true">
            {variant === "online_paid" ? "✓" : "📦"}
          </div>
          <h2 data-testid="sv2-bento-hero-title">{heroTitle}</h2>
          <p className="sv2-muted" data-testid="sv2-bento-hero-copy">
            {thankYou}
          </p>
          <p className="sv2-bento-order">
            {t(lang, "order_number")}
            <br />
            <span className="sv2-order-id" data-testid="sv2-public-order-number">
              {displayOrderNumber}
            </span>
          </p>
          <ul className="sv2-bento-meta">
            <li>
              <span>{t(lang, "amount")}</span>
              <strong>{amount}</strong>
            </li>
            <li>
              <span>{paymentRowLabel}</span>
              <strong data-testid="sv2-bento-payment-row">{paymentRow}</strong>
            </li>
            {variant === "fop_review" ? (
              <li>
                <span>{lang === "en" ? "Next step" : "Наступний крок"}</span>
                <strong data-testid="sv2-bento-next-row">{t(lang, "fop_next_step_manager")}</strong>
              </li>
            ) : (
              <li>
                <span>{lang === "en" ? "Next" : "Далі"}</span>
                <strong data-testid="sv2-bento-next-row">{fulfillmentNextRow}</strong>
              </li>
            )}
          </ul>
        </section>
      </div>

      <section className="sv2-bento-card" data-testid="sv2-status-card">
        <h3>{lang === "en" ? "Order status" : "Статус замовлення"}</h3>
        <p>{variant === "fop_review" ? t(lang, "fop_next_step_manager") : fulfillmentNextRow}</p>
        <p className="sv2-muted" style={{ fontSize: 12 }}>
          {variant === "cod"
            ? lang === "en"
              ? "Online payment is not required. Carrier settlement happens on delivery."
              : "Онлайн-оплата не потрібна. Розрахунок — при отриманні у Новій пошті."
            : lang === "en"
              ? "Tracking will appear after Nova Poshta shipment."
              : "Трекінг з’явиться після відправлення Новою Поштою."}
        </p>
      </section>

      <div className="sv2-success-action-stack sv2-postorder-actions" data-testid="sv2-success-action-stack">
        {onViewOrder ? (
          <button
            type="button"
            className="sv2-cta sv2-action-primary"
            onClick={onViewOrder}
            data-testid="sv2-bento-view-order"
          >
            {t(lang, "my_orders")}
          </button>
        ) : null}

        <div className="sv2-bento-referral" data-testid="sv2-paid-referral-slot">
          <ReferralCardV2
            lang={lang}
            referral={referral}
            surface="paid_bento"
            compact
            {...(onOpenRules ? { onOpenRules } : {})}
            {...(onReferralShared ? { onShared: onReferralShared } : {})}
          />
        </div>

        <button
          type="button"
          className="sv2-cta sv2-action-secondary"
          onClick={onCatalog ?? onHome}
          data-testid="sv2-bento-to-catalog"
        >
          {lang === "en" ? "To catalog" : "До каталогу"}
        </button>
        <button
          type="button"
          className="sv2-cta sv2-action-secondary"
          onClick={onContactManager}
          data-testid="sv2-bento-contact-manager"
        >
          {lang === "en" ? "Contact manager" : "Зв’язатися з менеджером"}
        </button>
        {onCloseStore ? (
          <button
            type="button"
            className="sv2-cta sv2-action-tertiary"
            onClick={onCloseStore}
            data-testid="sv2-close-store"
          >
            {lang === "en" ? "Close store" : "Закрити магазин"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
